import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ShieldCheck, Loader2, Sparkles, ChevronLeft, Star } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import { createCheckout, track } from "../lib/api";
import { getAnswers } from "../lib/onboarding";
import { ttTrack } from "../lib/analytics";
import { Brand } from "../components/NavBar";

const BENEFITS = [
  "Analyze conversation screenshots",
  "Explore possible manipulation & unhealthy communication patterns",
  "Understand confusing exchanges",
  "Gain insights into communication dynamics",
  "Find practical ways to respond & set boundaries",
];

export default function Paywall() {
  const navigate = useNavigate();
  const { isAuthed, user } = useAuth();
  const { is_pro, loaded } = useEntitlements();
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState("pro_annual");

  const answers = getAnswers();
  const firstName = (answers.first_name || "").trim();
  const otherName = (answers.other_name || "").trim();

  useEffect(() => {
    track("paywall_viewed");
    ttTrack("ViewContent", "pro_annual");
  }, []);

  const choose = (key) => {
    setSelected(key);
    ttTrack("AddToCart", key);
  };

  useEffect(() => {
    if (loaded && is_pro) navigate("/app", { replace: true });
  }, [loaded, is_pro, navigate]);

  const subscribe = async (key) => {
    track(key === "pro_annual" ? "yearly_selected" : "monthly_selected");
    ttTrack("AddToCart", key);
    if (!isAuthed) {
      toast("Create an account or sign in to start");
      navigate(`/sign-in?next=${encodeURIComponent("/paywall")}`);
      return;
    }
    setBusy(key);
    try {
      const { checkout_url } = await createCheckout(key);
      ttTrack("InitiateCheckout", key);
      window.location.href = checkout_url;
    } catch (e) {
      setBusy(null);
      if (String(e.message).toLowerCase().includes("sign in")) {
        navigate(`/sign-in?next=${encodeURIComponent("/paywall")}`);
      } else {
        toast.error(e.message || "Could not start checkout");
      }
    }
  };

  const headlineName = firstName || otherName;

  return (
    <>
      <header className="nav">
        <div className="container-nc nav-inner">
          <Brand onClick={() => navigate("/")} />
          <div style={{ display: "flex", gap: 8 }}>
            {user?.role === "admin" && (
              <button className="btn btn-ghost" onClick={() => navigate("/admin")} data-testid="paywall-admin-link">
                <ShieldCheck size={16} /> <span className="nav-label">Admin</span>
              </button>
            )}
            <button className="btn btn-ghost" onClick={() => navigate(-1)} data-testid="paywall-back">
              <ChevronLeft size={16} /> <span className="nav-label">Back</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container-nc" style={{ paddingTop: 28, paddingBottom: 90, maxWidth: 860 }}>
        <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto" }}>
          <span className="chip chip-brand" style={{ margin: "0 auto" }}>
            <Sparkles size={13} /> NarcCheck AI
          </span>
          <h1 style={{ fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 600, letterSpacing: "-0.03em", margin: "18px 0 12px", lineHeight: 1.1 }} data-testid="paywall-headline">
            Get Clarity on the Conversations That Matter.
          </h1>
          <p style={{ color: "var(--muted)", fontSize: 16, lineHeight: 1.6 }}>
            {headlineName ? <b style={{ color: "var(--on-surface)" }}>{firstName ? `${firstName}, ` : ""}</b> : null}
            understand confusing interactions{otherName ? ` with ${otherName}` : ""}, explore communication patterns, and get a
            clearer perspective on the conversations that leave you second-guessing yourself.
          </p>
        </div>

        {/* Benefits */}
        <div className="card card-pad" style={{ maxWidth: 460, margin: "26px auto 0" }}>
          {BENEFITS.map((p) => (
            <div key={p} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0" }}>
              <span style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--brand-tertiary)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                <Check size={13} color="var(--brand)" />
              </span>
              <span style={{ fontSize: 14.5 }}>{p}</span>
            </div>
          ))}
        </div>

        {/* Plans */}
        <div className="trait-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 28, maxWidth: 720, marginInline: "auto" }}>
          {/* Yearly (recommended) */}
          <div
            className="card card-pad"
            data-testid="plan-pro_annual"
            onClick={() => choose("pro_annual")}
            style={{
              position: "relative", display: "flex", flexDirection: "column", gap: 10, cursor: "pointer",
              borderColor: selected === "pro_annual" ? "var(--brand)" : "var(--border)",
              boxShadow: selected === "pro_annual" ? "0 0 0 1px var(--brand)" : "none",
            }}
          >
            <span className="chip chip-brand" style={{ position: "absolute", top: -12, right: 16 }}>
              <Star size={12} /> Best value
            </span>
            <div className="eyebrow">Yearly</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
              <span className="gauge-num" style={{ fontSize: 40 }}>$60</span>
              <span style={{ color: "var(--muted)", fontSize: 15 }}>/year</span>
            </div>
            <div className="mono" style={{ color: "var(--brand-2)", fontSize: 12.5, letterSpacing: "0.02em" }}>
              Just $5/month, billed yearly
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 600, color: "var(--on-surface)" }}>
              <span className="chip chip-neutral" style={{ color: "var(--brand-2)", borderColor: "var(--brand-2)" }}>3-day free trial</span>
            </div>
            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={(e) => { e.stopPropagation(); subscribe("pro_annual"); }}
              disabled={busy !== null}
              data-testid="subscribe-pro_annual"
              style={{ marginTop: 6 }}
            >
              {busy === "pro_annual" ? <Loader2 size={18} className="spin" /> : "Start My 3-Day Free Trial"}
            </button>
            <p style={{ color: "var(--muted)", fontSize: 11.5, lineHeight: 1.6, margin: 0 }} data-testid="annual-disclosure">
              3 days free, then $60/year. Your subscription automatically renews at $60/year until canceled.
              Cancel before your trial ends to avoid the first charge. $0 due today.
            </p>
          </div>

          {/* Monthly */}
          <div
            className="card card-pad"
            data-testid="plan-pro_monthly"
            onClick={() => choose("pro_monthly")}
            style={{
              display: "flex", flexDirection: "column", gap: 10, cursor: "pointer",
              borderColor: selected === "pro_monthly" ? "var(--brand)" : "var(--border)",
            }}
          >
            <div className="eyebrow">Monthly</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
              <span className="gauge-num" style={{ fontSize: 40 }}>$8.99</span>
              <span style={{ color: "var(--muted)", fontSize: 15 }}>/month</span>
            </div>
            <div className="mono" style={{ color: "var(--muted)", fontSize: 12.5 }}>No free trial</div>
            <div style={{ color: "var(--muted)", fontSize: 13 }}>Cancel anytime</div>
            <button
              className="btn btn-ghost btn-lg btn-block"
              onClick={(e) => { e.stopPropagation(); subscribe("pro_monthly"); }}
              disabled={busy !== null}
              data-testid="subscribe-pro_monthly"
              style={{ marginTop: 6 }}
            >
              {busy === "pro_monthly" ? <Loader2 size={18} className="spin" /> : "Subscribe for $8.99/Month"}
            </button>
            <p style={{ color: "var(--muted)", fontSize: 11.5, lineHeight: 1.6, margin: 0 }} data-testid="monthly-disclosure">
              $8.99 charged today, then renews automatically at $8.99/month until canceled.
            </p>
          </div>
        </div>

        <p className="mono" style={{ color: "var(--muted)", fontSize: 11, textAlign: "center", marginTop: 24, lineHeight: 1.7 }}>
          <ShieldCheck size={12} style={{ verticalAlign: "-2px" }} /> Secure checkout & subscription management by Stripe. Cancel anytime.
        </p>

        <div style={{ display: "flex", gap: 18, justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
          <span className="link" style={{ fontSize: 13 }} onClick={() => navigate("/terms")} data-testid="paywall-terms">Terms of Use</span>
          <span className="link" style={{ fontSize: 13 }} onClick={() => navigate("/privacy")} data-testid="paywall-privacy">Privacy Policy</span>
          <span className="link" style={{ fontSize: 13 }} onClick={() => navigate("/support")} data-testid="paywall-support">Support</span>
        </div>
        <p style={{ color: "var(--muted)", fontSize: 11.5, textAlign: "center", marginTop: 14, lineHeight: 1.6, maxWidth: 520, marginInline: "auto" }}>
          You can manage or cancel your subscription anytime from Settings. For personal reflection only — not professional, medical or legal advice.
        </p>
      </main>
    </>
  );
}
